import { Head } from 'vite-react-ssg';
import { SITE_NAME, SITE_TAGLINE, OG_IMAGE_PATH, absoluteUrl } from '@/lib/site';

export interface Crumb {
  name: string;
  href: string;
}

interface SEOProps {
  title: string;
  description: string;
  /** Site-relative path ('/upgrades/webcenter-14c-upgrade'). Canonical + og:url derive from it. */
  path: string;
  ogType?: 'website' | 'article';
  noindex?: boolean;
  breadcrumbs?: Crumb[];
}

// Serialize JSON-LD so a stray "</script>" or "<" in content can never break
// out of the script element.
export const jsonLd = (data: unknown) => JSON.stringify(data).replace(/</g, '\\u003c');

const SEO = ({ title, description, path, ogType = 'website', noindex = false, breadcrumbs }: SEOProps) => {
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const canonical = absoluteUrl(path);
  const ogImage = absoluteUrl(OG_IMAGE_PATH);

  const breadcrumbSchema =
    breadcrumbs && breadcrumbs.length
      ? {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: breadcrumbs.map((c, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: c.name,
            item: absoluteUrl(c.href),
          })),
        }
      : null;

  return (
    <Head>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />
      <link rel="canonical" href={canonical} />

      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={`${SITE_NAME} — ${SITE_TAGLINE}`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_US" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={`${SITE_NAME} — ${SITE_TAGLINE}`} />

      {breadcrumbSchema && <script type="application/ld+json">{jsonLd(breadcrumbSchema)}</script>}
    </Head>
  );
};

export default SEO;
